/**
 * 弹层占用一条 history，系统/侧滑后退时先关弹层（等同取消），避免遮罩残留在页面上。
 */
export type OverlayReleaseOptions = {
  /**
   * 关层后会立刻 `routeTo` 时传 true：只卸监听、去掉 MARK，不 `history.back()`，
   * 避免异步 back 冲掉紧随其后的新路由（如开户提示 → 开户文件）。
   */
  skipBack?: boolean;
};

export type OverlayHistoryHandle = {
  /** 主动关闭：默认若仍占着本层 history 条目则 back，不会再次触发 onBack */
  release: (options?: OverlayReleaseOptions) => void;
};

const MARK = '__difiOverlay';

/** 去掉 state 上的弹层 MARK；其余字段保留；没有其余字段则返回 null */
export const stripOverlayMark = (state: Record<string, unknown> | null) => {
  if (!state || typeof state !== 'object' || Array.isArray(state)) return null;
  const { [MARK]: _mark, ...rest } = state;
  return Object.keys(rest).length ? rest : null;
};

export function bindOverlayHistory(
  onBack: () => void,
  canClose?: () => boolean,
): OverlayHistoryHandle {
  const prev = window.history.state;
  const next =
    prev && typeof prev === 'object' && !Array.isArray(prev)
      ? { ...(prev as Record<string, unknown>), [MARK]: 1 }
      : { [MARK]: 1 };
  window.history.pushState(next, '', window.location.href);

  let released = false;

  const onPopState = () => {
    if (released) return;
    if (canClose && !canClose()) {
      window.history.pushState(next, '', window.location.href);
      return;
    }
    released = true;
    window.removeEventListener('popstate', onPopState);
    onBack();
  };

  window.addEventListener('popstate', onPopState);

  return {
    release: (options) => {
      if (released) return;
      released = true;
      window.removeEventListener('popstate', onPopState);
      const state = window.history.state as Record<string, unknown> | null;
      if (!state?.[MARK]) return;
      if (options?.skipBack) {
        window.history.replaceState(stripOverlayMark(state), '', window.location.href);
        return;
      }
      window.history.back();
    },
  };
}
